<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowsUpDownIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { Pin } from 'lucide-vue-next';
import { Checkbox } from '@/components/ui/checkbox';
import { GripVerticalIcon } from '@/components/ui/icons';
import DraggableContainer from '@/components/ui/draggable/DraggableContainer.vue';
import { usePersistentTable } from '@/composables/usePersistentTable';
import { cardDefaultColumnIdsFor, cardFixedColumnIdsFor } from '@/composables/searchInspectorRegistry';
import { fieldKindFor, propertyLabel } from '@/components/record/fields/fieldKind';
import { CUSTOM_LIST_SCHEDULE_FIELDS, customListScheduleFieldOf } from '@/components/FilterBuilder/filterControlRegistry';
import { NESTED_SCHEDULES_FIELD } from '@/composables/api/scheduleItems';
import { usePersistentTableStore, type MapCardFieldKey } from '@/stores/persistentTableStore';
import { columnDisplayOrdinal } from '@/utils/columnConfigSnapshot';
import { isRedundantForeignKeyColumn, ordinalLabel } from '@/utils/columnSettingsList';
import type { SortDirection, TableColumn, TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The Column settings panel. It lists the table's columns in two parts:
 * "Shown", in table order and draggable, and "Hidden", grouped or A–Z.
 *
 * Every click writes straight to the table's state. There is no Save and no
 * Cancel. A saved view still shows its "changed" dot, so a person can go back
 * to the saved view from there, and "Reset columns" puts the defaults back.
 *
 * On a map it lists the six address fields of the map's result cards instead.
 *
 * It is drawn as the Columns page of the list toolbar's Display menu. See
 * ListDisplayMenu.
 */
const props = withDefaults(
  defineProps<{
    additionalColumns?: TableColumnStaticProps[];
    mapMode?: boolean;
  }>(),
  { mapMode: false },
);


const persistentTableStore = usePersistentTableStore();
const {
  storeKey,
  entityId,
  columns,
  columnSort,
  toggleSort,
  updateColumnState,
  reorderColumns,
  tableLayout,
  mapCardFieldVisibility,
  setMapCardFieldVisibility,
  setCardColumnVisibility,
  resetColumnsToDefaults,
} = usePersistentTable({ additionalColumns: props.additionalColumns || [] });

/** One column as the panel draws it. */
type ColumnRow = {
  /** The column's id. DraggableContainer keys its items by it. */
  id: string;
  column: TableColumn;
  /** The Details panel's name for the field, such as "Account manager". */
  label: string;
  /** Shown in the current layout: in the table, or on the cards. */
  isShown: boolean;
  /** A has-many relation, such as an account's locations. */
  isRelatedList: boolean;
  /** 1 for the first sort, 2 for the second, or null when not sorted. */
  sortPosition: number | null;
};

/**
 * Card layouts draw the entity's fixed header fields whatever this panel says.
 * While one is active those columns sit in an "Always on cards" block at the
 * top, with no drag handle and no hide button. Pinning is a table feature, so
 * the pin buttons are hidden in card layouts too.
 */
const cardLayoutActive = computed(
  () => tableLayout.value === 'cards' || tableLayout.value === 'fixed-cards',
);

const fixedColumnIds = computed<string[]>(() =>
  cardLayoutActive.value ? cardFixedColumnIdsFor(storeKey) : [],
);

/**
 * The columns in the order the table draws them. `columns` is already sorted
 * that way, with pinned columns first, so Shown matches the table exactly.
 */
const columnsInTableOrder = computed(() => columns.value);

const sortPositionByField = computed(() => {
  const positions = new Map<string, number>();
  Object.keys(columnSort.value).forEach((field, index) => positions.set(field, index + 1));
  return positions;
});

/**
 * Whether a column is shown in the current layout. Card layouts keep their
 * own list, apart from the table's: a column is on the cards when the person
 * turned it on, or when it is one of the entity's card defaults and they have
 * not turned it off.
 */
function isShownInLayout(column: TableColumn): boolean {
  if (!cardLayoutActive.value) {
    return column.isVisible;
  }
  const explicitChoice = persistentTableStore.cardColumnVisible[column.id];
  return explicitChoice ?? cardDefaultColumnIdsFor(storeKey).includes(column.id);
}

function toColumnRow(column: TableColumn): ColumnRow {
  const kind = fieldKindFor(entityId.value, column);
  return {
    id: column.id,
    column,
    label: propertyLabel(entityId.value, column, kind),
    isShown: isShownInLayout(column),
    isRelatedList: kind === 'relatedList',
    sortPosition: sortPositionByField.value.get(column.field) ?? null,
  };
}

/**
 * Every column the panel can show or hide, in table order. The card-fixed
 * columns are left out, because the card always shows them. Card-only columns,
 * such as a player's Currently Playing, are left out of the table layout,
 * because the table cannot draw them. A custom list's schedule columns are left
 * out of the card layouts, because the card draws its schedules as lines of
 * their own.
 */
const listRows = computed<ColumnRow[]>(() => {
  const fixedIds = new Set(fixedColumnIds.value);
  return columnsInTableOrder.value
    .filter((column) => !fixedIds.has(column.id))
    .filter((column) => cardLayoutActive.value || !column.cardOnly)
    .filter((column) => !cardLayoutActive.value || customListScheduleFieldOf(column.key) === null)
    .map(toColumnRow);
});

/** The card-fixed columns, in the card's own order. */
const fixedRows = computed<ColumnRow[]>(() => {
  const fixedIds = fixedColumnIds.value;
  return columnsInTableOrder.value
    .filter((column) => fixedIds.includes(column.id))
    .sort((left, right) => fixedIds.indexOf(left.id) - fixedIds.indexOf(right.id))
    .map(toColumnRow);
});

const shownRows = computed(() => listRows.value.filter((row) => row.isShown));

const allColumnIds = computed(() => new Set(columns.value.map((column) => column.id)));

/**
 * The hidden columns the panel offers. A raw id column, such as "Account
 * manager user ID", is left out when the readable column for the same relation
 * is there. A shown id column stays in Shown, so it can still be hidden.
 */
const hiddenRows = computed(() =>
  listRows.value.filter(
    (row) => !row.isShown && !isRedundantForeignKeyColumn(row.column.id, allColumnIds.value),
  ),
);

// --- Search ------------------------------------------------------------------

const searchText = ref('');
const normalizedSearch = computed(() => searchText.value.trim().toLowerCase());

/** Search matches the name the panel shows, never the internal field path. */
function matchesSearch(label: string): boolean {
  return normalizedSearch.value === '' || label.toLowerCase().includes(normalizedSearch.value);
}

/** Escape clears the search first. Only an empty search lets the key reach the host. */
function onSearchEscape(event: KeyboardEvent) {
  if (searchText.value === '') {
    return;
  }
  event.stopPropagation();
  searchText.value = '';
}

const visibleShownCount = computed(
  () => shownRows.value.filter((row) => matchesSearch(row.label)).length,
);

// --- Hidden list order ---------------------------------------------------------

/**
 * Groups or A–Z, remembered per table. The stored value 'custom' means
 * Groups. See ColumnListSortMode in the store for why it was not renamed.
 */
const hiddenListMode = computed<'groups' | 'alphabetical'>({
  get: () => (persistentTableStore.columnListSortMode === 'alphabetical' ? 'alphabetical' : 'groups'),
  set: (mode) => {
    persistentTableStore.columnListSortMode = mode === 'alphabetical' ? 'alphabetical' : 'custom';
  },
});

type HiddenGroup = { title: string | null; rows: ColumnRow[] };

const hiddenGroups = computed<HiddenGroup[]>(() => {
  const matchingRows = hiddenRows.value.filter((row) => matchesSearch(row.label));
  if (hiddenListMode.value === 'alphabetical') {
    const sortedRows = [...matchingRows].sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: 'base' }),
    );
    return [{ title: null, rows: sortedRows }];
  }
  return [
    { title: 'Fields', rows: matchingRows.filter((row) => !row.isRelatedList) },
    { title: 'Related lists', rows: matchingRows.filter((row) => row.isRelatedList) },
  ].filter((group) => group.rows.length > 0);
});

const visibleHiddenCount = computed(() =>
  hiddenGroups.value.reduce((total, group) => total + group.rows.length, 0),
);

// --- Writing changes -----------------------------------------------------------

type ListEntry = { id: string; isVisible: boolean; isPinned: boolean };

function entryOf(row: ColumnRow): ListEntry {
  return { id: row.column.id, isVisible: row.column.isVisible, isPinned: row.column.isPinned };
}

/**
 * Write the list's order, pins and table visibility to the table state.
 *
 * This keeps every rule the old Save button kept:
 *
 * - Only the columns in the list are renumbered. The card-fixed columns and
 *   the card-only columns keep their own order.
 * - The card-fixed columns' table visibility is never touched. Writing it as
 *   "shown" once un-hid, in the table, columns the person had hidden there.
 *   That happened for buckets and custom lists, which pin the zone, the
 *   location and the parent bucket on their cards.
 * - In a card layout the table's own visibility is never touched either. The
 *   card's list of shown fields is a separate map, written with
 *   setCardColumnVisibility.
 *
 * Showing, hiding and dragging in a card layout do not come through here. See
 * showColumn and onShownReordered.
 */
function writeList(nextEntries: ListEntry[]) {
  const positionById = new Map(nextEntries.map((entry, index) => [entry.id, index]));
  const entryById = new Map(nextEntries.map((entry) => [entry.id, entry]));
  const updatedColumns = columns.value.map((column) => {
    const entry = entryById.get(column.id);
    const position = positionById.get(column.id);
    if (!entry || position === undefined) {
      return column;
    }
    return {
      ...column,
      isVisible: cardLayoutActive.value ? column.isVisible : entry.isVisible,
      isPinned: entry.isPinned,
      order: columnDisplayOrdinal(position, entry.isPinned),
    };
  });
  updateColumnState(updatedColumns);
}

function hideColumn(row: ColumnRow) {
  if (cardLayoutActive.value) {
    setCardColumnVisibility({ [row.column.id]: false });
    return;
  }
  writeList(
    listRows.value.map((listRow) =>
      listRow.column.id === row.column.id ? { ...entryOf(listRow), isVisible: false } : entryOf(listRow),
    ),
  );
}

/**
 * Where in Shown a column that is being turned on goes. Most columns go at the end.
 *
 * A custom list's schedule column goes right after the "schedules" column, after any
 * shown schedule columns there that come before it in the filter's order. Turn on Time
 * and then Date, and Date sits between "schedules" and Time. With "schedules" hidden it
 * goes at the end, like any column. It goes back beside "schedules" every time it is
 * turned on, even after the user dragged it somewhere else and hid it.
 */
function showIndexFor(row: ColumnRow, otherShownRows: ColumnRow[]): number {
  const scheduleField = customListScheduleFieldOf(row.column.key);
  // The "schedules" column's id is the nested schedules field.
  const schedulesIndex = otherShownRows.findIndex((shownRow) => shownRow.column.id === NESTED_SCHEDULES_FIELD);
  if (scheduleField === null || schedulesIndex === -1) {
    return otherShownRows.length;
  }
  const rank = CUSTOM_LIST_SCHEDULE_FIELDS.indexOf(scheduleField);
  let index = schedulesIndex + 1;
  while (index < otherShownRows.length) {
    const neighbourField = customListScheduleFieldOf(otherShownRows[index].column.key);
    if (neighbourField === null || CUSTOM_LIST_SCHEDULE_FIELDS.indexOf(neighbourField) > rank) {
      break;
    }
    index += 1;
  }
  return index;
}

/**
 * Showing a hidden column puts it at the end of Shown, or beside "schedules" for a
 * custom list's schedule column. See showIndexFor.
 *
 * In a card layout it only turns the field on for the cards. The field then
 * takes its place in the table's order, which is the order the cards draw in.
 * Moving it to the end would mean rewriting the table's order. That once left
 * a saved view marked as changed after the field was ticked and unticked
 * again, because unticking could not put the table's order back.
 */
function showColumn(row: ColumnRow) {
  if (cardLayoutActive.value) {
    setCardColumnVisibility({ [row.column.id]: true });
    return;
  }
  const otherRows = listRows.value.filter((listRow) => listRow.column.id !== row.column.id);
  const otherShownRows = otherRows.filter((listRow) => listRow.isShown);
  const showIndex = showIndexFor(row, otherShownRows);
  writeList([
    ...otherShownRows.slice(0, showIndex).map(entryOf),
    { ...entryOf(row), isVisible: true },
    ...otherShownRows.slice(showIndex).map(entryOf),
    ...otherRows.filter((listRow) => !listRow.isShown).map(entryOf),
  ]);
}

/**
 * A drag in a card layout reorders the card fields among themselves. The
 * dragged fields swap the places in the table's order that they already hold,
 * and every other column keeps its place. So dragging a field back puts the
 * table's order back exactly. Each column keeps its pin, because a pinned
 * column's place is stored as a negative number.
 */
function reorderCardFields(reorderedShownRows: ColumnRow[]) {
  const sortedPlaces = reorderedShownRows
    .map((row) => Math.abs(row.column.order))
    .sort((left, right) => left - right);
  const nextOrder: Record<string, number> = { ...persistentTableStore.columnOrder };
  reorderedShownRows.forEach((row, index) => {
    nextOrder[row.column.id] = sortedPlaces[index] * (row.column.isPinned ? -1 : 1);
  });
  reorderColumns(nextOrder);
}

/** A drag only reorders Shown. The hidden columns follow, in their old order. */
function onShownReordered(reorderedShownRows: ColumnRow[]) {
  if (cardLayoutActive.value) {
    reorderCardFields(reorderedShownRows);
    return;
  }
  writeList([
    ...reorderedShownRows.map(entryOf),
    ...listRows.value.filter((listRow) => !listRow.isShown).map(entryOf),
  ]);
}

function togglePin(row: ColumnRow) {
  writeList(
    listRows.value.map((listRow) =>
      listRow.column.id === row.column.id
        ? { ...entryOf(listRow), isPinned: !listRow.column.isPinned }
        : entryOf(listRow),
    ),
  );
}

/** Cycles none, then ascending, then descending, then none again. */
function cycleSort(row: ColumnRow) {
  if (row.column.isSortable) {
    toggleSort(row.column.field);
  }
}

function sortIconFor(direction: SortDirection | undefined) {
  if (direction === 'asc') return ArrowUpIcon;
  if (direction === 'desc') return ArrowDownIcon;
  return ArrowsUpDownIcon;
}

/** Keeps the wording the end-to-end tests look for, such as "Sort ID column asc (1)". */
function sortButtonLabel(row: ColumnRow): string {
  const direction = row.column.sortDirection;
  return direction
    ? `Sort ${row.label} column ${direction} (${row.sortPosition})`
    : `Sort ${row.label} column none`;
}

function sortChipDescription(row: ColumnRow): string {
  const direction = row.column.sortDirection === 'desc' ? 'descending' : 'ascending';
  return `Sorted ${ordinalLabel(row.sortPosition ?? 1)}, ${direction}`;
}

// --- Map mode ------------------------------------------------------------------

const mapFieldRows: { key: MapCardFieldKey; label: string }[] = [
  { key: 'address1', label: 'Address 1' },
  { key: 'address2', label: 'Address 2' },
  { key: 'city', label: 'City' },
  { key: 'stateSubdivision', label: 'State / Subdivision' },
  { key: 'zipPostal', label: 'Zip / Postal' },
  { key: 'country', label: 'Country' },
];

const visibleMapFieldRows = computed(() => mapFieldRows.filter((row) => matchesSearch(row.label)));

function setMapField(key: MapCardFieldKey, isShown: boolean) {
  setMapCardFieldVisibility({ ...mapCardFieldVisibility.value, [key]: isShown });
}

const panelAriaLabel = computed(() =>
  props.mapMode
    ? 'Choose the address fields on map result cards. Changes apply as you make them.'
    : 'Choose which columns are shown, their order, sort and pins. Changes apply as you make them.',
);

/**
 * The hover-revealed buttons on a row. They also show while anything in the
 * row has keyboard focus, and always on a touch screen, which has no hover.
 */
const revealOnRowHover =
  'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';
const iconButtonClass =
  'flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-foreground/[0.08] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
/**
 * Rows sit straight on the panel, like the Details rows, with a hover tint.
 * The tints are see-through foreground rather than bg-muted, because in dark
 * mode the inspector's surface is the same colour as --muted.
 */
const rowClass =
  'group -mx-2 flex h-8 items-center gap-2 rounded-md px-2 text-sm transition-colors hover:bg-foreground/[0.05] focus-within:bg-foreground/[0.05]';
const sortChipClass =
  'inline-flex h-5 shrink-0 items-center gap-0.5 rounded-full bg-foreground/[0.08] px-1.5 text-[11px] text-muted-foreground';
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col"
    :data-testid="mapMode ? 'column-settings-map-mode' : 'column-settings-panel'"
    :aria-label="panelAriaLabel"
  >
    <div class="shrink-0 px-4 pb-2 pt-2">
      <label
        class="flex h-8 items-center gap-2 rounded-lg bg-foreground/[0.06] px-2.5 focus-within:ring-1 focus-within:ring-foreground/20"
      >
        <MagnifyingGlassIcon class="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
        <input
          v-model="searchText"
          type="search"
          :placeholder="mapMode ? 'Search fields' : 'Search columns'"
          :aria-label="mapMode ? 'Search map card fields' : 'Search columns'"
          data-testid="column-settings-search"
          class="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
          @keydown.esc="onSearchEscape"
        />
        <button
          v-if="searchText"
          type="button"
          class="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
          aria-label="Clear search"
          @click="searchText = ''"
        >
          <XMarkIcon class="size-4" aria-hidden="true" />
        </button>
      </label>
    </div>

    <div id="column-config-scrollable" class="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
      <!-- Map mode: the six address fields of a map result card. -->
      <div v-if="mapMode" data-testid="column-settings-map-field-list">
        <label
          v-for="row in visibleMapFieldRows"
          :key="row.key"
          :class="[rowClass, 'cursor-pointer']"
          data-testid="column-settings-map-field-row"
        >
          <Checkbox
            :model-value="mapCardFieldVisibility[row.key]"
            :aria-label="`Show ${row.label} on map result cards`"
            class="shrink-0"
            @update:model-value="(value: boolean | 'indeterminate') => setMapField(row.key, value === true)"
          />
          <span class="min-w-0 flex-1 truncate">{{ row.label }}</span>
        </label>
        <p v-if="visibleMapFieldRows.length === 0" class="py-2 text-sm text-muted-foreground">
          No field matches “{{ searchText.trim() }}”.
        </p>
      </div>

      <div v-else data-testid="column-settings-column-field-list">
        <!-- Card layouts: the fields every card draws, whatever this list says. -->
        <section
          v-if="fixedRows.length > 0"
          v-show="fixedRows.some((row) => matchesSearch(row.label))"
          class="mb-2"
          aria-label="Always on cards"
        >
          <h3 class="flex h-7 items-center text-xs font-medium text-muted-foreground">Always on cards</h3>
          <div
            v-for="row in fixedRows"
            v-show="matchesSearch(row.label)"
            :key="`fixed-${row.column.id}`"
            :class="rowClass"
            data-testid="column-settings-fixed-row"
          >
            <span class="min-w-0 flex-1 truncate text-muted-foreground">{{ row.label }}</span>
            <span
              v-if="row.column.sortDirection && row.sortPosition"
              :class="sortChipClass"
              data-testid="column-settings-sort-chip"
            >
              <span aria-hidden="true">{{ ordinalLabel(row.sortPosition) }}</span>
              <component :is="sortIconFor(row.column.sortDirection)" class="size-3" aria-hidden="true" />
              <span class="sr-only">{{ sortChipDescription(row) }}</span>
            </span>
            <button
              v-if="row.column.isSortable"
              type="button"
              :class="[iconButtonClass, revealOnRowHover]"
              :aria-label="sortButtonLabel(row)"
              @click="cycleSort(row)"
            >
              <component :is="sortIconFor(row.column.sortDirection)" class="size-3.5" aria-hidden="true" />
            </button>
          </div>
        </section>

        <!-- Shown: the columns in the table, in table order. -->
        <section aria-label="Shown columns">
          <h3 class="flex h-7 items-center justify-between text-xs font-medium text-muted-foreground">
            <span data-testid="column-settings-shown-heading">
              Shown <span class="text-muted-foreground/70">· {{ shownRows.length }}</span>
            </span>
            <span v-if="shownRows.length > 1" class="font-normal text-muted-foreground/70">Drag to reorder</span>
          </h3>
          <DraggableContainer
            :model-value="shownRows"
            item-key="id"
            handle=".drag-handle"
            scrollable-container="#column-config-scrollable"
            @update:model-value="onShownReordered"
          >
            <template #item="{ element: row }">
              <div
                v-show="matchesSearch(row.label)"
                :class="rowClass"
                data-testid="column-settings-row"
                :data-column-id="row.column.id"
              >
                <div
                  class="drag-handle -ml-1 flex shrink-0 cursor-grab touch-none select-none items-center text-muted-foreground/70 hover:text-foreground"
                  :aria-label="`Drag to reorder ${row.label}`"
                  data-testid="column-settings-drag-handle"
                >
                  <GripVerticalIcon size="sm" />
                </div>
                <span class="min-w-0 flex-1 truncate" data-testid="column-settings-row-label">{{ row.label }}</span>
                <span
                  v-if="row.column.sortDirection && row.sortPosition"
                  :class="sortChipClass"
                  data-testid="column-settings-sort-chip"
                >
                  <span aria-hidden="true">{{ ordinalLabel(row.sortPosition) }}</span>
                  <component :is="sortIconFor(row.column.sortDirection)" class="size-3" aria-hidden="true" />
                  <span class="sr-only">{{ sortChipDescription(row) }}</span>
                </span>
                <!-- A pinned column keeps its pin lit, so the pin is visible
                     without hovering. Card layouts have no pins. -->
                <button
                  v-if="!cardLayoutActive"
                  type="button"
                  :class="[iconButtonClass, row.column.isPinned ? 'text-foreground' : revealOnRowHover]"
                  :aria-label="`${row.column.isPinned ? 'Unpin' : 'Pin'} ${row.label} column`"
                  :aria-pressed="row.column.isPinned"
                  data-testid="column-settings-pin"
                  @click="togglePin(row)"
                >
                  <Pin class="size-3.5" :stroke-width="1.75" aria-hidden="true" />
                </button>
                <button
                  v-if="row.column.isSortable"
                  type="button"
                  :class="[iconButtonClass, revealOnRowHover]"
                  :aria-label="sortButtonLabel(row)"
                  data-testid="column-settings-sort"
                  @click="cycleSort(row)"
                >
                  <component :is="sortIconFor(row.column.sortDirection)" class="size-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  :class="[iconButtonClass, revealOnRowHover]"
                  :aria-label="`Hide ${row.label} column`"
                  data-testid="column-settings-hide"
                  @click="hideColumn(row)"
                >
                  <EyeSlashIcon class="size-3.5" aria-hidden="true" />
                </button>
              </div>
            </template>
          </DraggableContainer>
          <p v-if="shownRows.length === 0" class="py-1 text-sm text-muted-foreground">
            No columns shown. Pick one below.
          </p>
          <p v-else-if="visibleShownCount === 0" class="py-1 text-sm text-muted-foreground">
            No shown column matches.
          </p>
        </section>

        <!-- Hidden: click a column to add it to the end of Shown. -->
        <section v-if="hiddenRows.length > 0" class="mt-3" aria-label="Hidden columns">
          <h3 class="flex h-7 items-center justify-between text-xs font-medium text-muted-foreground">
            <span data-testid="column-settings-hidden-heading">
              Hidden <span class="text-muted-foreground/70">· {{ hiddenRows.length }}</span>
            </span>
            <span class="flex gap-0.5 rounded-lg bg-foreground/[0.06] p-0.5 font-normal" role="group" aria-label="Hidden column order">
              <button
                type="button"
                class="rounded-md px-2 py-0.5 transition-colors"
                :class="hiddenListMode === 'groups' ? 'bg-background text-foreground shadow-sm dark:bg-foreground/[0.12]' : 'hover:text-foreground'"
                :aria-pressed="hiddenListMode === 'groups'"
                data-testid="column-list-sort-groups"
                @click="hiddenListMode = 'groups'"
              >
                Groups
              </button>
              <button
                type="button"
                class="rounded-md px-2 py-0.5 transition-colors"
                :class="hiddenListMode === 'alphabetical' ? 'bg-background text-foreground shadow-sm dark:bg-foreground/[0.12]' : 'hover:text-foreground'"
                :aria-pressed="hiddenListMode === 'alphabetical'"
                data-testid="column-list-sort-alphabetical"
                @click="hiddenListMode = 'alphabetical'"
              >
                A–Z
              </button>
            </span>
          </h3>
          <div v-for="group in hiddenGroups" :key="group.title ?? 'all'">
            <h4 v-if="group.title" class="pb-0.5 pt-1.5 text-[11px] text-muted-foreground/70">{{ group.title }}</h4>
            <button
              v-for="row in group.rows"
              :key="row.column.id"
              type="button"
              :class="[rowClass, 'w-[calc(100%+1rem)] text-left text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring']"
              :aria-label="`Show ${row.label} column`"
              data-testid="column-settings-hidden-row"
              :data-column-id="row.column.id"
              @click="showColumn(row)"
            >
              <PlusIcon class="size-3.5 shrink-0" aria-hidden="true" />
              <span class="min-w-0 flex-1 truncate">{{ row.label }}</span>
              <span
                v-if="row.column.sortDirection && row.sortPosition"
                :class="sortChipClass"
              >
                <span aria-hidden="true">{{ ordinalLabel(row.sortPosition) }}</span>
                <component :is="sortIconFor(row.column.sortDirection)" class="size-3" aria-hidden="true" />
              </span>
            </button>
          </div>
          <p v-if="visibleHiddenCount === 0" class="py-1 text-sm text-muted-foreground">
            No hidden column matches.
          </p>
        </section>
      </div>
    </div>

    <div class="flex shrink-0 items-center justify-between gap-3 border-t px-4 py-3">
      <span class="text-xs text-muted-foreground">Changes apply as you make them</span>
      <button
        v-if="!mapMode"
        type="button"
        class="rounded text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid="column-settings-reset"
        @click="resetColumnsToDefaults"
      >
        Reset columns
      </button>
    </div>
  </div>
</template>
